/** @jsxImportSource @emotion/react */
import {
  EuiButton,
  EuiFlexGroup,
  EuiFlexItem,
  EuiSplitPanel,
  EuiText,
} from "@inscopix/ideas-eui";
import { css } from "@emotion/react";
import { useRouteMapContext } from "providers/RouteMapProvider/RouteMapProvider";
import { useTenantContext } from "providers/TenantProvider/TenantProvider";
import { ToolPanelCreateFirstDraft } from "./panels/ToolPanelCreateFirstDraft";
import { ToolStatus } from "types/constants";
import { ToolPanelContinueFirstDraft } from "./panels/ToolPanelContinueFirstDraft";
import { useToolDataContext } from "pages/tool/ToolDataProvider";
import { ToolPanelAbout } from "./panels/ToolPanelAbout";
import { ToolPanelAccess } from "./panels/ToolPanelAccess";
import { ToolPanelResources } from "./panels/ToolPanelResources";
import { ToolPanelVersions } from "./panels/ToolPanelVersions";

// use an owl (> * + *) selector to add a border between each child
const sidebarStyles = css`
  > * + * {
    position: relative;
    margin-top: 16px;
    &::before {
      content: "";
      display: block;
      position: absolute;
      top: -8px;
      left: 0;
      right: 0;
      height: 1px;
      background: #d3dae6;
    }
  }
`;

/*
 * Tool homepage tab displaying version information
 */
export const ToolTabTool = () => {
  const { tool } = useToolDataContext();
  const currentTenantKey = useTenantContext((s) => s.currentTenant.key);
  const routeMap = useRouteMapContext((s) => s.routeMap);

  const goToSettingsTab = () => {
    routeMap.TOOL_SETTINGS.dynamicPath({
      tenantKey: currentTenantKey,
      toolId: tool.id,
    }).navigateToReplace();
  };

  const latestVersion = tool.latest_tool_version;

  return (
    <EuiFlexGroup>
      <EuiFlexItem grow={8}>
        <EuiFlexGroup direction="column" gutterSize="s">
          {tool.project !== null &&
            latestVersion !== null &&
            tool.latest_tool_version?.status === ToolStatus.DRAFT && (
              <EuiFlexItem grow={false}>
                <EuiSplitPanel.Outer
                  direction="row"
                  color={"subdued"}
                  hasShadow={false}
                  hasBorder={true}
                >
                  <EuiSplitPanel.Inner paddingSize="s">
                    <EuiText style={{ padding: "4px" }} color="subdued">
                      <p>Unpublished draft currently in progress</p>
                    </EuiText>
                  </EuiSplitPanel.Inner>
                  <EuiSplitPanel.Inner paddingSize="s">
                    <EuiText textAlign="right">
                      <EuiButton
                        size="s"
                        fill
                        onClick={() =>
                          routeMap.TOOL_EDITOR.dynamicPath({
                            tenantKey: currentTenantKey,
                            toolId: tool.id,
                            toolVersionId: latestVersion.id,
                          }).navigateTo()
                        }
                      >
                        Continue Editing Draft
                      </EuiButton>
                    </EuiText>
                  </EuiSplitPanel.Inner>
                </EuiSplitPanel.Outer>
              </EuiFlexItem>
            )}
          <EuiFlexItem grow={false}>
            {(() => {
              if (latestVersion === null) {
                return <ToolPanelCreateFirstDraft />;
              }
              if (
                tool.tool_versions.length === 1 &&
                tool.tool_versions[0].status === ToolStatus.DRAFT
              ) {
                return <ToolPanelContinueFirstDraft />;
              }

              return (
                <ToolPanelVersions
                  tool={{ ...tool, latest_tool_version: latestVersion }}
                />
              );
            })()}
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiFlexItem>
      <EuiFlexItem grow={2} css={sidebarStyles}>
        <ToolPanelAbout
          goToSettingsTab={tool.project !== null ? goToSettingsTab : undefined}
        />
        {tool.project !== null && (
          <>
            <ToolPanelResources />
            <ToolPanelAccess projectId={tool.project.id} />
          </>
        )}
      </EuiFlexItem>
    </EuiFlexGroup>
  );
};
